import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { AnimatedCard } from "./AnimatedCard"
import { useApexState } from "@/hooks/useApexState"
import { 
  Package, 
  Search, 
  Truck,
  CheckCircle,
  Clock,
  RotateCcw,
  Download,
  Star,
  ChevronRight,
  Crown
} from "lucide-react"

interface OrderItem {
  id: string
  productId: string
  name: string
  price: number
  quantity: number
  size: string
  color: string
  image: string
  isApexLocked?: boolean
}

interface Order {
  id: string
  orderNumber: string
  date: string
  status: 'processing' | 'shipped' | 'delivered' | 'cancelled'
  total: number
  items: OrderItem[]
  trackingNumber?: string
  estimatedDelivery?: string
  pointsEarned: number
  canReorder: boolean
  canReturn: boolean
  isReviewed: boolean
}

interface OrderHistoryProps {
  isOpen: boolean
  onClose: () => void
}

const mockOrders: Order[] = [
  {
    id: "1",
    orderNumber: "APX-2024-001234",
    date: "2024-01-15",
    status: "delivered",
    total: 350,
    items: [
      {
        id: "1",
        productId: "apex-joggers",
        name: "Apex Kinetic Joggers",
        price: 180,
        quantity: 1,
        size: "L",
        color: "Charcoal",
        image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=150&h=150&fit=crop",
        isApexLocked: true
      },
      {
        id: "2",
        productId: "form-tank",
        name: "Form Training Tank",
        price: 85,
        quantity: 2,
        size: "M",
        color: "Sage",
        image: "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=150&h=150&fit=crop"
      }
    ],
    trackingNumber: "1Z999AA1234567890",
    pointsEarned: 525,
    canReorder: true,
    canReturn: true,
    isReviewed: false
  },
  {
    id: "2",
    orderNumber: "APX-2024-001235",
    date: "2024-01-20",
    status: "shipped",
    total: 220,
    items: [
      {
        id: "3",
        productId: "architect-hoodie",
        name: "Architect Hoodie",
        price: 220,
        quantity: 1,
        size: "L",
        color: "Black",
        image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=150&h=150&fit=crop",
        isApexLocked: true
      }
    ],
    trackingNumber: "1Z999AA1234567891",
    estimatedDelivery: "Jan 25, 2024",
    pointsEarned: 330,
    canReorder: true,
    canReturn: false,
    isReviewed: false
  },
  {
    id: "3",
    orderNumber: "APX-2024-001236",
    date: "2024-01-22",
    status: "processing",
    total: 280,
    items: [
      {
        id: "4",
        productId: "urban-jacket",
        name: "Urban Explorer Jacket",
        price: 280,
        quantity: 1,
        size: "M",
        color: "Navy",
        image: "https://images.unsplash.com/photo-1551028719-00167b16eac5?w=150&h=150&fit=crop",
        isApexLocked: true
      }
    ],
    pointsEarned: 420,
    canReorder: true,
    canReturn: false,
    isReviewed: false
  }
]

export function OrderHistory({ isOpen, onClose }: OrderHistoryProps) {
  const { state } = useApexState()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedStatus, setSelectedStatus] = useState<string>("all")
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null)

  const statusOptions = [
    { id: "all", label: "All Orders", count: mockOrders.length },
    { id: "processing", label: "Processing", count: mockOrders.filter(o => o.status === 'processing').length },
    { id: "shipped", label: "Shipped", count: mockOrders.filter(o => o.status === 'shipped').length },
    { id: "delivered", label: "Delivered", count: mockOrders.filter(o => o.status === 'delivered').length }
  ]

  const filteredOrders = mockOrders.filter(order => {
    const matchesSearch = order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.items.some(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    const matchesStatus = selectedStatus === "all" || order.status === selectedStatus
    return matchesSearch && matchesStatus
  })

  const getStatusConfig = (status: Order['status']) => {
    switch (status) {
      case 'processing':
        return { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Processing' }
      case 'shipped':
        return { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Shipped' }
      case 'delivered':
        return { icon: CheckCircle, color: 'text-accent', bg: 'bg-accent/10', label: 'Delivered' }
      case 'cancelled':
        return { icon: Clock, color: 'text-destructive', bg: 'bg-destructive/10', label: 'Cancelled' }
    }
  }

  const handleReorder = (order: Order) => {
    console.log('Reordering:', order.id)
    // Add reorder logic
  }

  const handleTrackOrder = (order: Order) => {
    console.log('Tracking order:', order.trackingNumber)
    // Open tracking in new tab or modal
  }

  const handleDownloadReceipt = (order: Order) => {
    console.log('Downloading receipt for:', order.id)
    // Download receipt logic
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="absolute top-0 right-0 w-full max-w-2xl h-full bg-background border-l border-border">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Package size={20} className="text-foreground" />
              <h2 className="text-lg font-semibold text-foreground">Order History</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              ✕
            </Button>
          </div>

          {/* Search and Filters */}
          <div className="p-4 space-y-4 border-b border-border">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search orders or products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-input border-border"
              />
            </div>
            
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {statusOptions.map(option => (
                <Button
                  key={option.id}
                  variant={selectedStatus === option.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedStatus(option.id)}
                  className="whitespace-nowrap"
                >
                  {option.label} ({option.count})
                </Button>
              ))}
            </div>
          </div>

          {/* Orders List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Package size={48} className="text-muted-foreground opacity-50 mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No orders found
                </h3>
                <p className="text-muted-foreground">
                  {searchQuery ? "Try a different search term" : "You haven't placed any orders yet"}
                </p>
              </div>
            ) : (
              filteredOrders.map(order => {
                const statusConfig = getStatusConfig(order.status)
                const StatusIcon = statusConfig.icon
                const isExpanded = expandedOrder === order.id

                return (
                  <AnimatedCard key={order.id}>
                    <Card className="bg-card border-border">
                      <CardHeader 
                        className="cursor-pointer"
                        onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <div className={`p-2 rounded-lg ${statusConfig.bg}`}>
                                <StatusIcon size={16} className={statusConfig.color} />
                              </div>
                              <div>
                                <h3 className="font-semibold text-foreground">
                                  {order.orderNumber}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(order.date).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <Badge className={`${statusConfig.color} ${statusConfig.bg} border-0`}>
                                {statusConfig.label}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                              </span>
                              <span className="text-lg font-semibold text-primary">
                                ${order.total}
                              </span>
                            </div>
                          </div>
                          
                          <ChevronRight 
                            size={20} 
                            className={`text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                          />
                        </div>
                      </CardHeader>

                      {isExpanded && (
                        <CardContent className="pt-0 space-y-4">
                          {/* Order Items */}
                          <div className="space-y-3">
                            {order.items.map(item => (
                              <div key={item.id} className="flex gap-3 p-3 rounded-lg bg-muted/20">
                                <div className="w-12 h-12 rounded-lg overflow-hidden">
                                  <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <h4 className="font-medium text-foreground text-sm">
                                        {item.name}
                                      </h4>
                                      <p className="text-xs text-muted-foreground">
                                        {item.color} • {item.size} • Qty: {item.quantity}
                                      </p>
                                      {item.isApexLocked && (
                                        <Badge className="bg-primary/20 text-primary text-xs mt-1">
                                          <Crown size={8} className="mr-1" />
                                          Apex Exclusive
                                        </Badge>
                                      )}
                                    </div>
                                    <span className="text-sm font-semibold text-foreground">
                                      ${item.price}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Order Details */}
                          <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/10">
                            <div>
                              <p className="text-xs text-muted-foreground">Points Earned</p>
                              <p className="font-semibold text-accent">+{order.pointsEarned}</p>
                            </div>
                            {order.trackingNumber && (
                              <div>
                                <p className="text-xs text-muted-foreground">Tracking</p>
                                <p className="font-mono text-xs text-foreground">{order.trackingNumber}</p>
                              </div>
                            )}
                            {order.estimatedDelivery && (
                              <div>
                                <p className="text-xs text-muted-foreground">Est. Delivery</p>
                                <p className="text-sm text-foreground">{order.estimatedDelivery}</p>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 pt-2">
                            {order.canReorder && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleReorder(order)}
                              >
                                <RotateCcw size={14} className="mr-1" />
                                Reorder
                              </Button>
                            )}
                            
                            {order.trackingNumber && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleTrackOrder(order)}
                              >
                                <Truck size={14} className="mr-1" />
                                Track
                              </Button>
                            )}
                            
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDownloadReceipt(order)}
                            >
                              <Download size={14} className="mr-1" />
                              Receipt
                            </Button>

                            {order.status === 'delivered' && !order.isReviewed && (
                              <Button 
                                size="sm"
                                className="bg-accent hover:bg-accent/90 text-accent-foreground ml-auto"
                              >
                                <Star size={14} className="mr-1" />
                                Review
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  </AnimatedCard>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}